import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';

interface QRGeneratorProps {
  couponId: number;
  customerPhone?: string;
}

interface GeneratedQR {
  token: string;
  qrData: string;
  qrImageUrl: string;
  coupon: {
    id: number;
    title: string;
    description: string;
    discount: string;
    vendor_name: string;
    expires_in_minutes: number;
  };
}

export const QRGenerator: React.FC<QRGeneratorProps> = ({ couponId, customerPhone }) => {
  const [qrData, setQrData] = useState<GeneratedQR | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateQR = async () => {
    if (!couponId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('https://cf-api.b-9f2.workers.dev/api/qr/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          coupon_id: couponId,
          customer_phone: customerPhone,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate QR code');
      }

      // Generate QR code image from token
      const qrImageUrl = await QRCode.toDataURL(data.token, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff',
        },
      });

      setQrData({
        ...data,
        qrImageUrl,
      });

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    generateQR();
  }, [couponId, customerPhone]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-gray-600">Gerando código QR...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <div className="text-red-600 mr-3">❌</div>
          <div>
            <h3 className="text-red-800 font-medium">Erro ao gerar QR Code</h3>
            <p className="text-red-600 text-sm mt-1">{error}</p>
          </div>
        </div>
        <button
          onClick={generateQR}
          className="mt-3 bg-red-600 text-white px-4 py-2 rounded text-sm hover:bg-red-700 transition-colors"
        >
          Tentar Novamente
        </button>
      </div>
    );
  }

  if (!qrData) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
        <p className="text-gray-600">Selecione um cupom para gerar o QR Code</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="text-center mb-4">
        <img
          src={qrData.qrImageUrl}
          alt="QR Code"
          className="mx-auto mb-4 border rounded-lg"
        />
        <h3 className="text-lg font-semibold text-gray-900">{qrData.coupon.title}</h3>
        <p className="text-gray-600 text-sm">{qrData.coupon.vendor_name}</p>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Desconto:</span>
          <span className="font-medium text-green-600">{qrData.coupon.discount}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Expira em:</span>
          <span className="font-medium text-orange-600">{qrData.coupon.expires_in_minutes} minutos</span>
        </div>
      </div>

      {qrData.coupon.description && (
        <div className="bg-gray-50 rounded p-3 mb-4">
          <p className="text-sm text-gray-700">{qrData.coupon.description}</p>
        </div>
      )}

      <div className="border-t pt-4">
        <p className="text-xs text-gray-500 mb-2">Token JWT (para desenvolvimento):</p>
        <code className="text-xs bg-gray-100 p-2 rounded block break-all">
          {qrData.token.substring(0, 50)}...
        </code>
      </div>

      <button
        onClick={generateQR}
        className="w-full mt-4 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors"
      >
        🔄 Gerar Novo QR Code
      </button>
    </div>
  );
};